import { Check, Loader2 } from 'lucide-react';
import { Badge, Button, Card, CardContent } from '@design-system';
import { CheckoutLayout, useTrackAdvisorCheckoutViewed } from '@client/features/advisor-onboarding';
import { usePlanSelector } from './usePlanSelector';

interface PlanSelectorCardProps {
  advisorId: string;
  className?: string;
  variant?: 'start' | 'restart';
}

export function PlanSelectorCard({
  advisorId,
  className,
  variant = 'start',
}: PlanSelectorCardProps) {
  const {
    viewState,
    selectedPlanType,
    isPending,
    pendingPlanType,
    isCheckoutLoading,
    clientSecret,
    checkoutContent,
    trialDetailFormatted,
    handleSelectPlan,
    handleBackToPlans,
  } = usePlanSelector({ advisorId });

  useTrackAdvisorCheckoutViewed(advisorId, selectedPlanType, viewState === 'checkout');

  if (viewState === 'checkout') {
    return (
      <CheckoutLayout
        content={checkoutContent}
        trialDetailFormatted={trialDetailFormatted}
        onBack={handleBackToPlans}
        isLoading={isCheckoutLoading}
        clientSecret={clientSecret}
      />
    );
  }

  const title = variant === 'restart' ? 'Restart Your Subscription' : 'Start Your Subscription';
  const description =
    variant === 'restart'
      ? 'Choose a plan to reactivate your Waterlily subscription.'
      : 'Choose a plan to get started with Waterlily.';

  return (
    <div className={className}>
      <div className="flex flex-col gap-5">
        <div>
          <h2 className="text-primary-dark text-lg font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="relative pt-2">
            <Badge
              variant="default"
              className="bg-primary absolute left-2 top-0 z-10 rounded-full px-2 py-0.5 text-[10px] text-white sm:left-3"
            >
              SAVE 20%
            </Badge>
            <Card className="hover:border-primary-200 h-full border-gray-200 transition-all hover:shadow-md">
              <CardContent className="p-4">
                <h4 className="text-base font-semibold text-gray-900">Annual</h4>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900">$47</span>
                  <span className="text-xs text-gray-600">/mo</span>
                </div>
                <p className="text-xs text-gray-500">Billed annually</p>

                <ul className="mt-3 space-y-1.5">
                  <li className="flex items-center gap-2 text-xs text-gray-600">
                    <Check className="text-primary size-3 shrink-0" />
                    <span>Full access to all features</span>
                  </li>
                  <li className="flex items-center gap-2 text-xs text-gray-600">
                    <Check className="text-primary size-3 shrink-0" />
                    <span>Best value</span>
                  </li>
                </ul>
                <Button
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary-50 mt-4 w-full rounded-lg text-xs"
                  size="sm"
                  onClick={() => handleSelectPlan('annual')}
                  disabled={isPending}
                >
                  {pendingPlanType === 'annual' ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    'Choose annual'
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="pt-2">
            <Card className="hover:border-primary-200 h-full border-gray-200 transition-all hover:shadow-md">
              <CardContent className="p-4">
                <h4 className="text-base font-semibold text-gray-900">Monthly</h4>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900">$59</span>
                  <span className="text-xs text-gray-600">/mo</span>
                </div>
                <p className="text-xs text-gray-500">No commitment</p>

                <ul className="mt-3 space-y-1.5">
                  <li className="flex items-center gap-2 text-xs text-gray-600">
                    <Check className="text-primary size-3 shrink-0" />
                    <span>Full access to all features</span>
                  </li>
                  <li className="flex items-center gap-2 text-xs text-gray-600">
                    <Check className="text-primary size-3 shrink-0" />
                    <span>Cancel anytime</span>
                  </li>
                </ul>
                <Button
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary-50 mt-4 w-full rounded-lg text-xs"
                  size="sm"
                  onClick={() => handleSelectPlan('monthly')}
                  disabled={isPending}
                >
                  {pendingPlanType === 'monthly' ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    'Choose monthly'
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
